import type { ComponentProps } from "react";
import { useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  PatientDataItemName,
  SnackBarType,
  StatusLabelForCsSystemType,
} from "../../../types";
import { PageBody } from "../PageBody";
import { Typography } from "../../atoms/Typography";
import { List, ListItem } from "../../atoms/List";
import { hexToRGB } from "../../../utils/style";
import { GQLPrescription } from "@rx-cs-system/schema";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defaultTheme } from "../../themes";
//import { defaultTheme } from "../../themes";
import { Snackbar } from "../../atoms/Snackbar";
import okIcon from "../../../assets/icons/ok.png";
import errorIcon from "../../../assets/icons/alert.png";
import { StatusLabelMap } from "../../../utils/prescription";

/**
 * 処方詳細ページ
 */

type Patient = {
  patientId: string;
  name: string;
  memberImage: any;
  birthday: string;
  appName: string;
  phoneNumber?: string | null;
  email?: string | null;
  coCheckerSerial?: string | null;
  prescriptionCode: string;
  prescribedAt: string;
  registeredAt: string;
  medicalInstitutionId: string;
  medicalInstitutionName: string;
  prescription?: GQLPrescription | null;
};

export type PatientProps = {
  patient: Patient;
  patientItems?: PatientDataItemName[];
  onCancelPrescriptionCodeButtonClick: () => void;
  snackBar: SnackBarType;
};

const Menu = styled.div`
  padding: 10px 0;
  width: auto;
`;

const MenuInner = styled.div`
  cursor: pointer;
  padding: 0 30px;
  display: flex;
  flex-flow: column nowrap;
`;

const MenuItem = styled.div`
  margin: 15px 0;
  display: flex;
  align-items: center;
`;

const ContentWrapper = styled.div`
  padding: 0 40px;
`;

const Inner = styled.div`
  padding: 40px 0 20px;
  display: flex;
  ${(props) => hexToRGB(props.theme.colors.primaryBorder, 0.5)};
`;

const LeftBlock = styled.div`
  flex: 1;
  padding-right: 40px;
`;

const MemberIconAndName = styled.div`
  display: flex;
  align-items: center;
  img {
    margin-right: 20px;
  }
`;

const RightBlock = styled.div`
  flex: 1;
  padding-left: 40px;
`;

const PaddingRightWrapper = styled.div`
  width: 100%;
  padding-right: 20px;
`;

const StyledLink = styled(Link)`
  color: ${(props) => props.theme.colors.brand};
`;

const TextRightTypography = (props: ComponentProps<typeof Typography>) => (
  <PaddingRightWrapper>
    <Typography textRight size={2}>
      {props.children}
    </Typography>
  </PaddingRightWrapper>
);

export function PatientScDetailPage({
  patient,
  onCancelPrescriptionCodeButtonClick,
  snackBar,
}: PatientProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      <PageBody
        title="処方詳細"
        buttonTitle="メニュー"
        buttonIcon={
          <FontAwesomeIcon icon={"pen"} color={defaultTheme.colors.brand} />
        }
        onButtonClick={() => {
          setIsMenuOpen(!isMenuOpen);
        }}
        menuListVisible={isMenuOpen}
        menuList={
          <Menu>
            <MenuInner>
              <MenuItem onClick={onCancelPrescriptionCodeButtonClick}>
                <Typography size={2}>処方のキャンセル</Typography>
              </MenuItem>
            </MenuInner>
          </Menu>
        }
        onHideMenu={() => {
          setIsMenuOpen(false);
        }}
      >
        <>
          <ContentWrapper>
            <Inner>
              <LeftBlock>
                <MemberIconAndName>
                  <img
                    src={patient.memberImage}
                    width="72"
                    height="72"
                    alt={"user_icon"}
                  />
                  <Typography size={5} breakWord>
                    {patient.name}
                  </Typography>
                </MemberIconAndName>
              </LeftBlock>
            </Inner>
          </ContentWrapper>

          <ContentWrapper>
            <Inner>
              <LeftBlock>
                <List>
                  <ListItem>
                    <Typography bold size={1}>
                      id
                    </Typography>
                    <TextRightTypography>
                      {patient.patientId}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      メールアドレス
                    </Typography>
                    <TextRightTypography>{patient.email}</TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      処方日
                    </Typography>
                    <TextRightTypography>
                      {patient.prescribedAt}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      アプリ名
                    </Typography>
                    <TextRightTypography>{patient.appName}</TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      COチェッカーシリアル
                    </Typography>
                    <TextRightTypography>
                      {patient.coCheckerSerial}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      処方コードステータス
                    </Typography>
                    <TextRightTypography>
                      {
                        StatusLabelMap[
                          patient.prescription?.prescriptionCureAppPdtCode
                            ?.statusLabelForCsSystem as StatusLabelForCsSystemType
                        ]
                      }
                    </TextRightTypography>
                  </ListItem>
                </List>
              </LeftBlock>
              <RightBlock>
                <List>
                  <ListItem>
                    <Typography bold size={1}>
                      電話番号
                    </Typography>
                    <TextRightTypography>
                      {patient.phoneNumber}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      生年月日
                    </Typography>
                    <TextRightTypography>
                      {patient.birthday}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      処方コード
                    </Typography>
                    <TextRightTypography>
                      {patient.prescriptionCode}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      アプリ利用開始日
                    </Typography>
                    <TextRightTypography>
                      {patient.registeredAt}
                    </TextRightTypography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      処方医療機関
                    </Typography>
                    <TextRightTypography>
                      <StyledLink
                        to={`/medicalInstitution/${patient.medicalInstitutionId}`}
                      >
                        {patient.medicalInstitutionName}
                      </StyledLink>
                    </TextRightTypography>
                  </ListItem>
                </List>
              </RightBlock>
            </Inner>
          </ContentWrapper>
        </>
      </PageBody>

      {!!snackBar && (
        <Snackbar
          isOpen={snackBar.isOpen}
          icon={
            <img
              src={snackBar.type === "ok" ? okIcon : errorIcon}
              width="18"
              height="18"
              alt={snackBar.type}
            />
          }
          text={snackBar.message}
          close={snackBar.close}
        />
      )}
    </>
  );
}
